import { makeStyles } from 'tss-react/mui';
import {
    TemplateHeader,
    TemplateColumn,
    TimesheetDay,
    TimesheetTemplate,
} from 'api/report/types';
import { Summary } from 'report/logic/timesheet/calculations';
import { extractHoursAndMinutes } from 'utils/utils_legacy';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Skeleton from '@mui/material/Skeleton';
import TextField from 'components/interface/textField/TextField';
import {
    extractNumber,
    extractMinutesFromHoursDecimals,
    minutesToHoursDecimals,
} from 'utils/utils';
import { WEEKDAY } from '../utils';
import { red, pink } from '@mui/material/colors';
import { Box, lighten } from '@mui/material';
import Tooltip from 'components/interface/tooltip/Tooltip';
import Symbol from 'components/Symbol';

const useStyles = makeStyles()(theme => ({
    table: {
        marginBottom: theme.spacing(2),
        background: '#fff',
    },
    headerCell: {
        background: theme.palette.primary.main,
        color: '#fff',
        fontSize: '14px',
    },
    subheaderCell: {
        background: theme.palette.grey[200],
        color: theme.palette.grey[800],
        fontSize: '14px',
        fontWeight: '400',
    },
    cell: {
        height: '48px',
        wordBreak: 'break-word',
        hyphens: 'auto',
        textAlign: 'center',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        border: '1px solid ' + theme.palette.grey[800],
    },
    cellSums: {
        background: theme.palette.primary['200'],
    },
    cellSumsNormalHours: {
        background: theme.palette.primary[50],
    },
    cellSumSickHours: {
        background: lighten(red[50], 0.4),
    },
    publicHoliday: {
        color: pink[900],
        background: pink[50],
    },
    isSickHours: {
        background: lighten(red[50], 0.4),
        borderTop: '2px dotted ' + theme.palette.grey[500],
    },
    sickHoursIcon: {
        color: red[900],
        paddingLeft: theme.spacing(1),
    },
    splitTextFieldWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    splitTextField: {
        flexBasis: '45%',
    },
    thickBorderTop: {
        borderTop: '2px solid ' + theme.palette.grey[800],
    },
    notMaching: {
        background: red[300],
    },
    noPadding: {
        padding: 0,
    },
}));

interface Props {
    days: TimesheetDay[];
    template: TimesheetTemplate;
    totalHours: string;
    summary: Summary;
    summaryNormalHours?: Summary;
    summarySickHours?: Summary;
    useSplitTextfields: boolean;
    canSubmit: boolean;
    disabled: boolean;
    loading: boolean;
    enableSickHours: boolean;
    previewMatcherFunction?: (
        dayIndex: number,
        columnIndex: number
    ) => null | number;
    onUpdateTimesheetDay?: (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => void;
}

export default function NormalTimesheet({
    days,
    template,
    totalHours,
    summary,
    summaryNormalHours,
    summarySickHours,
    useSplitTextfields,
    canSubmit,
    disabled,
    loading,
    enableSickHours,
    previewMatcherFunction,
    onUpdateTimesheetDay,
}: Props): JSX.Element {
    const { classes, cx } = useStyles();
    const subheaders = template.subheader || [];

    const renderHeaderCell = (
        { text, colspan }: TemplateHeader,
        key: number
    ) => {
        return (
            <TableCell
                align='center'
                key={key}
                colSpan={parseInt(colspan)}
                className={cx(classes.headerCell, classes.cell)}>
                {text}
            </TableCell>
        );
    };

    const renderSubheaderCell = ({ text }: TemplateColumn, key: number) => {
        return (
            <TableCell
                align='center'
                key={key}
                colSpan={1}
                className={cx(classes.subheaderCell, classes.cell)}
                dangerouslySetInnerHTML={{ __html: text }}></TableCell>
        );
    };

    const renderFooter = () => {
        const { totalTitle, totalColspan, footnote } = template;
        return (
            <TableRow>
                <TableCell className={classes.cell}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='96px'
                        />
                    ) : (
                        totalTitle
                    )}
                </TableCell>
                <TableCell
                    colSpan={totalColspan}
                    className={cx(classes.cell, classes.cellSums)}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='96px'
                        />
                    ) : (
                        totalHours || ''
                    )}
                </TableCell>
                <TableCell
                    colSpan={
                        footnote?.colspan ? parseInt(footnote?.colspan) : 4
                    }
                    className={classes.cell}
                    {...(loading
                        ? {}
                        : {
                              dangerouslySetInnerHTML: {
                                  __html: footnote?.text || '',
                              },
                          })}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='300px'
                        />
                    ) : null}
                </TableCell>
            </TableRow>
        );
    };

    const renderPage = (): JSX.Element => {
        return (
            <Table
                className={classes.table}
                padding='none'
                style={{ tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow>
                        {(template.header ?? []).map((col, i) =>
                            renderHeaderCell(col, i)
                        )}
                    </TableRow>
                    <TableRow>
                        {renderSubheaderCell(
                            {
                                text: '',
                                maxHours: 0,
                                isJour: false,
                                interval: [],
                            },
                            -1
                        )}
                        {subheaders.map((col, i) =>
                            renderSubheaderCell(col, i)
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {days.map((day, index) => (
                        <Row
                            key={index}
                            {...day}
                            subheaders={subheaders}
                            index={index}
                            canSubmit={canSubmit}
                            disabled={disabled}
                            useSplitTextfields={useSplitTextfields}
                            loading={loading}
                            enableSickHours={enableSickHours}
                            onUpdateTimesheetDay={onUpdateTimesheetDay}
                            previewMatcherFunction={previewMatcherFunction}
                        />
                    ))}
                    <SummaryRow
                        columns={summary}
                        summaryNormalHours={summaryNormalHours}
                        summarySickHours={summarySickHours}
                        loading={loading}
                    />

                    {renderFooter()}
                </TableBody>
            </Table>
        );
    };

    return renderPage();
}

interface RowProps extends TimesheetDay {
    subheaders: TemplateColumn[];
    index: number;
    canSubmit: boolean;
    disabled: boolean;
    useSplitTextfields: boolean;
    loading: boolean;
    enableSickHours: boolean;
    previewMatcherFunction?: (
        dayIndex: number,
        columnIndex: number
    ) => null | number;
    onUpdateTimesheetDay?: (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => void;
}

const Row = ({
    month,
    day,
    weekday,
    holiday,
    hours,
    sickHours,
    subheaders,
    index,
    canSubmit,
    disabled,
    useSplitTextfields,
    loading,
    enableSickHours,
    previewMatcherFunction,
    onUpdateTimesheetDay,
}: RowProps) => {
    const { classes, cx } = useStyles();

    const handleUpdateTimesheetDay = (
        value: string,
        columnIndex: number,
        type: 'hour' | 'minutes',
        isSickHours: boolean
    ) => {
        let maxHours = 24;
        if (subheaders[columnIndex].maxHours) {
            maxHours = subheaders[columnIndex].maxHours;
        }

        const minFloat = (a: number, b: number) =>
            a > b ? extractNumber(b + '', 3) : extractNumber(a + '', 3);

        const key = isSickHours ? 'sickHours' : 'hours';

        onUpdateTimesheetDay &&
            onUpdateTimesheetDay(prev => {
                const d = { ...prev };
                d[key] = [...d[key]];

                if (useSplitTextfields) {
                    // when using split textfields you enter hours and minutes separate. Therefore the logic is special.
                    if (type === 'hour') {
                        value = extractNumber(value, 3);
                        value = parseFloat(value) > 24 ? '24' : value + '';
                        const hour = value ? parseInt(value) : 0;
                        const minutesDecimals = extractMinutesFromHoursDecimals(
                            d[key][columnIndex]
                        ); // This turns for example 4.25 into 0.25, it only keeps the decimals
                        d[key][columnIndex] = minFloat(
                            hour + minutesDecimals,
                            maxHours
                        );
                    } else {
                        value = extractNumber(value, 3);
                        value = parseFloat(value) > 59 ? '59' : value + '';
                        const hour = d[key][columnIndex]
                            ? parseInt(d[key][columnIndex])
                            : 0;
                        const minutesDecimals = minutesToHoursDecimals(value); // This turns for example 15 into 0.25
                        d[key][columnIndex] = minFloat(
                            hour + minutesDecimals,
                            maxHours
                        );
                    }
                } else {
                    value = extractNumber(value, 3);
                    d[key][columnIndex] =
                        parseFloat(value) > maxHours
                            ? maxHours + ''
                            : value + '';
                }

                return d;
            }, index);
    };

    const buildDayCell = (
        value: string,
        columnIndex: number,
        isSickHours: boolean
    ) => {
        const splitTime = useSplitTextfields
            ? extractHoursAndMinutes(value)
            : null;
        // this will turn 0 into an empty string
        const hourText = useSplitTextfields
            ? splitTime?.hours
                ? splitTime?.hours?.toString()
                : ''
            : value;
        const minutesText = useSplitTextfields
            ? splitTime?.minutes
                ? splitTime?.minutes?.toString()
                : ''
            : '';

        if (canSubmit) {
            return (
                <TableCell
                    key={columnIndex}
                    colSpan={1}
                    className={cx(classes.cell, classes.noPadding, {
                        [classes.isSickHours]: isSickHours,
                    })}>
                    <Box
                        sx={{
                            display: 'flex',
                        }}>
                        <TextField
                            disabled={disabled}
                            value={hourText || ''}
                            onChange={v =>
                                handleUpdateTimesheetDay(
                                    v,
                                    columnIndex,
                                    'hour',
                                    isSickHours
                                )
                            }
                            endAdornment={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        paddingLeft: 1,
                                    }}>
                                    <Typography variant='body2' color='inherit'>
                                        h
                                    </Typography>
                                    {isSickHours && columnIndex === 0 ? (
                                        <Symbol
                                            size='md'
                                            symbol='sick'
                                            color='red-dark'
                                            tooltip='Sjuktimmar'
                                        />
                                    ) : null}
                                </Box>
                            }
                            variantSx={{
                                backgroundColor: isSickHours
                                    ? 'transparent'
                                    : undefined,
                            }}
                            variant='sheet'
                            height={48}
                            inputSx={{
                                textAlign: 'right',
                            }}
                            onKeyDown={event => {
                                // disables navigation
                                if (
                                    enableSickHours &&
                                    event.key.startsWith('Arrow')
                                ) {
                                    event.stopPropagation();
                                    event.preventDefault();
                                }
                            }}
                            width={useSplitTextfields ? 50 : undefined}
                            fullWidth
                        />

                        {useSplitTextfields ? (
                            <TextField
                                disabled={disabled}
                                value={minutesText || ''}
                                onChange={v =>
                                    handleUpdateTimesheetDay(
                                        v,
                                        columnIndex,
                                        'minutes',
                                        isSickHours
                                    )
                                }
                                endAdornment={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1,
                                            paddingLeft: 1,
                                        }}>
                                        <Typography
                                            variant='body2'
                                            color='inherit'>
                                            m
                                        </Typography>
                                    </Box>
                                }
                                endAdornmentSx={{
                                    pointerEvents: 'unset',
                                }}
                                variantSx={{
                                    backgroundColor: isSickHours
                                        ? 'transparent'
                                        : undefined,
                                }}
                                variant='sheet'
                                height={48}
                                inputSx={{
                                    textAlign: 'right',
                                }}
                                onKeyDown={event => {
                                    // disables navigation
                                    if (
                                        enableSickHours &&
                                        event.key.startsWith('Arrow')
                                    ) {
                                        event.stopPropagation();
                                        event.preventDefault();
                                    }
                                }}
                                width={useSplitTextfields ? 50 : undefined}
                                fullWidth
                            />
                        ) : null}
                    </Box>
                </TableCell>
            );
        } else {
            const isPreviewing = !!previewMatcherFunction;
            const matchHoursExceeding =
                isPreviewing && previewMatcherFunction(index, columnIndex);
            let localValue = value;
            if (
                isPreviewing &&
                matchHoursExceeding !== null &&
                matchHoursExceeding !== 0
            ) {
                localValue = `> ${matchHoursExceeding}`;
            }
            return (
                <TableCell
                    key={columnIndex}
                    colSpan={1}
                    className={cx(classes.cell, {
                        [classes.isSickHours]: isSickHours,
                        [classes.notMaching]:
                            isPreviewing && matchHoursExceeding === null,
                    })}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='96px'
                        />
                    ) : (
                        localValue
                    )}
                </TableCell>
            );
        }
    };

    return (
        <>
            <TableRow>
                <TableCell
                    className={cx(classes.cell, {
                        [classes.publicHoliday]: holiday?.IsPublicHoliday,
                    })}
                    rowSpan={enableSickHours ? 2 : 1}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='96px'
                        />
                    ) : (
                        <Tooltip
                            enterTouchDelay={500}
                            title={holiday?.Name}
                            placement='top'
                            disableInteractive>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexShrink: 0,
                                    wordBreak: 'normal',
                                }}>
                                <Typography variant='body2' color='inherit'>
                                    {`${WEEKDAY[weekday]} ${day}/${month}`}
                                </Typography>
                                {holiday?.IsMajorHoliday ? (
                                    <Typography
                                        variant='caption'
                                        color='inherit'>
                                        storhelg
                                    </Typography>
                                ) : null}
                            </Box>
                        </Tooltip>
                    )}
                </TableCell>
                {hours.map((v, i) => buildDayCell(v, i, false))}
            </TableRow>
            {enableSickHours ? (
                <TableRow>
                    {sickHours.map((v, i) => buildDayCell(v, i, true))}
                </TableRow>
            ) : null}
        </>
    );
};

interface SummaryRowProps {
    columns: Summary;
    summaryNormalHours?: Summary;
    summarySickHours?: Summary;
    loading: boolean;
}

const SummaryRow = ({
    columns,
    summaryNormalHours,
    summarySickHours,
    loading,
}: SummaryRowProps) => {
    const { classes, cx } = useStyles();

    return (
        <>
            <TableRow>
                <TableCell className={cx(classes.cell, classes.thickBorderTop)}>
                    <Typography variant='body2' color='inherit'>
                        Summa antal timmar
                    </Typography>
                </TableCell>
                {columns.map((v, i) => (
                    <TableCell
                        key={i}
                        colSpan={1}
                        className={cx(
                            classes.cell,
                            classes.cellSums,
                            classes.thickBorderTop
                        )}>
                        {loading ? (
                            <Skeleton
                                animation='pulse'
                                variant='text'
                                width='96px'
                            />
                        ) : (
                            v
                        )}
                    </TableCell>
                ))}
            </TableRow>
            {summaryNormalHours && (
                <TableRow>
                    <TableCell className={classes.cell}>
                        <Typography variant='body2' color='inherit'>
                            Summa normala timmar
                        </Typography>
                    </TableCell>
                    {summaryNormalHours.map((v, i) => (
                        <TableCell
                            key={i}
                            colSpan={1}
                            className={cx(
                                classes.cell,
                                classes.cellSums,
                                classes.cellSumsNormalHours
                            )}>
                            {loading ? (
                                <Skeleton
                                    animation='pulse'
                                    variant='text'
                                    width='96px'
                                />
                            ) : (
                                v
                            )}
                        </TableCell>
                    ))}
                </TableRow>
            )}
            {summarySickHours && (
                <TableRow>
                    <TableCell className={classes.cell}>
                        <Typography variant='body2' color='inherit'>
                            Summa sjuktimmar
                        </Typography>
                    </TableCell>
                    {summarySickHours.map((v, i) => (
                        <TableCell
                            key={i}
                            colSpan={1}
                            className={cx(
                                classes.cell,
                                classes.cellSums,
                                classes.cellSumSickHours
                            )}>
                            {loading ? (
                                <Skeleton
                                    animation='pulse'
                                    variant='text'
                                    width='96px'
                                />
                            ) : (
                                v
                            )}
                        </TableCell>
                    ))}
                </TableRow>
            )}
        </>
    );
};
